export interface SemanticGraphEntry {
    id: string;
    title: string;
    category: string;
    summary: string;
    href: string;
    keywords?: string[];
}

export interface SemanticGraphRankedEntry<T extends SemanticGraphEntry> {
    entry: T;
    score: number;
    relevance: number;
    matchedTerms: string[];
}

const TRANSFORMERS_MODULE_URL = 'https://cdn.jsdelivr.net/npm/@xenova/transformers@2.17.2';
const TRANSFORMERS_WASM_PATH = 'https://cdn.jsdelivr.net/npm/@xenova/transformers@2.17.2/dist/';
const EMBEDDING_MODEL_ID = 'Xenova/multilingual-e5-small';
const MAX_MODEL_TEXT_LENGTH = 1800;
const MAX_UPLOADED_TEXT_LENGTH = 12000;
const WORKER_RANK_TIMEOUT_MS = 45000;

export const SEMANTIC_GRAPH_ACCEPTED_FILE_TYPES = '.pdf,.docx,.pptx,.xlsx,.txt,.md,.csv,.json';
export const HWP_PDF_CONVERSION_MESSAGE = 'HWP는 PDF로 변환한 파일을 업로드하세요';

export interface SemanticGraphFileText {
    text: string;
    summary: string;
    usedFallback: boolean;
    requiresPdfConversion?: boolean;
}

export async function extractSemanticGraphFileText(file: File): Promise<SemanticGraphFileText> {
    if (isHwpDocumentFile(file)) {
        return {
            text: '',
            summary: HWP_PDF_CONVERSION_MESSAGE,
            usedFallback: true,
            requiresPdfConversion: true,
        };
    }

    const buffer = await readFileBuffer(file);
    const fallback = file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim() || file.name;
    const candidates: string[] = [];

    if (isZipBuffer(buffer) || /\.(docx|pptx|xlsx)$/i.test(file.name)) {
        candidates.push(await extractZipText(buffer));
    }

    if (/\.pdf$/i.test(file.name) || file.type === 'application/pdf') {
        candidates.push(extractPdfText(buffer));
    }

    if (isTextLikeFile(file) || (!isKnownStructuredFile(file) && !candidates.some(Boolean))) {
        candidates.push(decodeBestEffort(buffer));
    }

    const content = normalizeDocumentText(candidates.filter(Boolean).join(' '));
    const usefulContent = isUsefulDocumentText(content) ? content : '';
    const text = (usefulContent || fallback).slice(0, MAX_UPLOADED_TEXT_LENGTH);
    const summary = summarizeDocumentText(usefulContent, fallback);

    return {
        text,
        summary,
        usedFallback: !usefulContent,
    };
}

export const isHwpDocumentFile = (file: File): boolean => (
    /\.(hwp|hwpx)$/i.test(file.name)
    || /hwp|hangul/i.test(file.type)
);

const readFileBuffer = (file: File): Promise<ArrayBuffer> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error || new Error('File read failed'));
    reader.readAsArrayBuffer(file);
});

const isTextLikeFile = (file: File): boolean => (
    file.type.startsWith('text/')
    || /\.(txt|md|csv|json|html?|xml|log)$/i.test(file.name)
);

const isKnownStructuredFile = (file: File): boolean => (
    file.type === 'application/pdf'
    || /\.(pdf|docx|pptx|xlsx)$/i.test(file.name)
);

const isZipBuffer = (buffer: ArrayBuffer): boolean => {
    const bytes = new Uint8Array(buffer);
    return bytes.length > 4 && bytes[0] === 0x50 && bytes[1] === 0x4b;
};

const extractZipText = async (buffer: ArrayBuffer): Promise<string> => {
    const bytes = new Uint8Array(buffer);
    const view = new DataView(buffer);
    const chunks: string[] = [];
    let offset = 0;

    while (offset + 30 < bytes.length) {
        if (view.getUint32(offset, true) !== 0x04034b50) {
            offset += 1;
            continue;
        }

        const method = view.getUint16(offset + 8, true);
        const compressedSize = view.getUint32(offset + 18, true);
        const fileNameLength = view.getUint16(offset + 26, true);
        const extraLength = view.getUint16(offset + 28, true);
        const nameStart = offset + 30;
        const nameEnd = nameStart + fileNameLength;
        const dataStart = nameEnd + extraLength;

        if (nameEnd > bytes.length || dataStart > bytes.length) {
            break;
        }

        const name = new TextDecoder('utf-8').decode(bytes.slice(nameStart, nameEnd));
        const dataEnd = compressedSize > 0 ? Math.min(dataStart + compressedSize, bytes.length) : dataStart;

        if (compressedSize > 0 && isRelevantZipTextEntry(name)) {
            const compressed = bytes.slice(dataStart, dataEnd);
            const inflated = method === 0 ? compressed : method === 8 ? await inflateRaw(compressed) : undefined;

            if (inflated && inflated.length) {
                chunks.push(decodeBestEffort(inflated.buffer));
            }
        }

        offset = dataEnd > offset ? dataEnd : offset + 30;
    }

    return chunks.join(' ');
};

const isRelevantZipTextEntry = (name: string): boolean => (
    /^docProps\/[^/]+\.xml$/i.test(name)
    || /^word\/(document|header\d*|footer\d*|footnotes|endnotes)\.xml$/i.test(name)
    || /^ppt\/(slides|notesSlides)\/[^/]+\.xml$/i.test(name)
    || /^xl\/(sharedStrings|worksheets\/[^/]+)\.xml$/i.test(name)
);

const inflateRaw = async (data: Uint8Array): Promise<Uint8Array | undefined> => {
    const DecompressionStreamCtor = (globalThis as any).DecompressionStream;

    if (!DecompressionStreamCtor || typeof Blob === 'undefined' || typeof Response === 'undefined') {
        return undefined;
    }

    try {
        const stream = new Blob([data]).stream().pipeThrough(new DecompressionStreamCtor('deflate-raw'));
        return new Uint8Array(await new Response(stream).arrayBuffer());
    } catch (_) {
        return undefined;
    }
};

const extractPdfText = (buffer: ArrayBuffer): string => {
    const raw = decodeBestEffort(buffer);
    const chunks: string[] = [];
    const literalPattern = /\((?:\\.|[^\\()]){2,}\)\s*Tj/g;
    const arrayPattern = /\[((?:\((?:\\.|[^\\()])*\)|[^\]])+)\]\s*TJ/g;
    let match: RegExpExecArray | null;

    while ((match = literalPattern.exec(raw))) {
        chunks.push(unescapePdfLiteral(match[0]));
    }

    while ((match = arrayPattern.exec(raw))) {
        const literals = match[1].match(/\((?:\\.|[^\\()])*\)/g) || [];
        chunks.push(literals.map(unescapePdfLiteral).join(' '));
    }

    return chunks.join(' ');
};

const unescapePdfLiteral = (value: string): string => value
    .replace(/^\(/, '')
    .replace(/\)\s*(Tj)?$/, '')
    .replace(/\\([nrtbf()\\])/g, ' ')
    .replace(/\\[0-7]{1,3}/g, ' ');

const decodeBestEffort = (buffer: ArrayBuffer): string => {
    const encodings = ['utf-8', 'utf-16le', 'euc-kr'];
    let best = '';
    let bestScore = -Infinity;

    encodings.forEach((encoding) => {
        try {
            const decoded = new TextDecoder(encoding).decode(buffer);
            const score = readableScore(decoded);

            if (score > bestScore) {
                best = decoded;
                bestScore = score;
            }
        } catch (_) {
            // Ignore unsupported browser encodings.
        }
    });

    return best;
};

const readableScore = (value: string): number => {
    const readable = (value.match(/[0-9A-Za-z가-힣]/g) || []).length;
    const replacement = (value.match(/\uFFFD/g) || []).length;
    const controls = (value.match(/[\x00-\x08\x0E-\x1F]/g) || []).length;
    return readable - replacement * 12 - controls * 3;
};

const normalizeDocumentText = (value: string): string => decodeHtmlEntities(value)
    .replace(/<[^>]+>/g, ' ')
    .replace(/\\u[0-9a-f]{4}/gi, ' ')
    .replace(/[\x00-\x08\x0E-\x1F]/g, ' ')
    .replace(/[^0-9A-Za-z가-힣ㄱ-ㅎㅏ-ㅣ\s.,;:!?%/+#[\]()_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const decodeHtmlEntities = (value: string): string => value
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

const isUsefulDocumentText = (value: string): boolean => (
    value.length >= 8 && extractKeywordTokens(value).length >= 2
);

const summarizeDocumentText = (content: string, fallback: string): string => {
    if (!content) {
        return `${fallback} 파일명`;
    }

    const keywords = extractKeywordTokens(content).slice(0, 8);
    return keywords.length ? keywords.join(' ') : content.slice(0, 120);
};

const STOP_WORDS = new Set([
    'the', 'and', 'for', 'with', 'from', 'this', 'that', 'have', 'will', 'are', 'was',
    '및', '또는', '그리고', '대한', '위한', '있는', '합니다', '입니다', '으로', '에서',
]);

const extractKeywordTokens = (content: string): string[] => {
    const counts = new Map<string, number>();

    content
        .toLowerCase()
        .split(/[^0-9a-zA-Z가-힣]+/)
        .map((token) => token.trim())
        .filter((token) => token.length > 1 && !STOP_WORDS.has(token))
        .forEach((token) => counts.set(token, (counts.get(token) || 0) + 1));

    return Array.from(counts.entries())
        .sort((left, right) => {
            if (right[1] !== left[1]) {
                return right[1] - left[1];
            }

            return right[0].length - left[0].length;
        })
        .map(([token]) => token);
};

class SemanticGraphWorkerClient {
    private worker?: Worker;
    private workerUrl?: string;
    private nextRequestId = 0;
    private pending = new Map<number, {
        resolve: (items: SemanticGraphRankedEntry<SemanticGraphEntry>[]) => void;
        reject: (error: Error) => void;
        timer: number;
    }>();

    public async rank<T extends SemanticGraphEntry>(
        seed: string,
        entries: T[],
    ): Promise<SemanticGraphRankedEntry<T>[]> {
        const worker = this.getWorker();

        if (!worker) {
            throw new Error('Semantic graph worker is not available');
        }

        const requestId = this.nextRequestId + 1;
        this.nextRequestId = requestId;

        return new Promise((resolve, reject) => {
            const timer = window.setTimeout(() => {
                this.pending.delete(requestId);
                reject(new Error('Semantic graph worker timed out'));
            }, WORKER_RANK_TIMEOUT_MS);

            this.pending.set(requestId, {
                resolve: resolve as (items: SemanticGraphRankedEntry<SemanticGraphEntry>[]) => void,
                reject,
                timer,
            });

            worker.postMessage({
                id: requestId,
                type: 'rank',
                seed,
                entries,
            });
        });
    }

    private getWorker(): Worker | undefined {
        if (this.worker) {
            return this.worker;
        }

        if (typeof Worker === 'undefined' || typeof Blob === 'undefined' || typeof URL === 'undefined') {
            return undefined;
        }

        try {
            const blob = new Blob([SEMANTIC_GRAPH_WORKER_SOURCE], { type: 'text/javascript' });
            this.workerUrl = URL.createObjectURL(blob);
            this.worker = new Worker(this.workerUrl, { type: 'module' });
            this.worker.onmessage = (event) => this.handleMessage(event);
            this.worker.onerror = (event) => {
                this.reset(new Error(event.message || 'Semantic graph worker failed'));
            };
        } catch (error) {
            this.reset(error instanceof Error ? error : new Error(String(error)));
        }

        return this.worker;
    }

    private handleMessage(event: MessageEvent<{
        id: number;
        type: string;
        ok: boolean;
        items?: SemanticGraphRankedEntry<SemanticGraphEntry>[];
        error?: string;
    }>) {
        const message = event.data;

        if (!message || message.type !== 'rank') {
            return;
        }

        const pending = this.pending.get(message.id);

        if (!pending) {
            return;
        }

        window.clearTimeout(pending.timer);
        this.pending.delete(message.id);

        if (message.ok) {
            pending.resolve(message.items || []);
            return;
        }

        pending.reject(new Error(message.error || 'Semantic graph worker failed'));
    }

    private reset(error: Error) {
        this.pending.forEach((pending) => {
            window.clearTimeout(pending.timer);
            pending.reject(error);
        });
        this.pending.clear();

        if (this.worker) {
            this.worker.terminate();
            this.worker = undefined;
        }

        if (this.workerUrl) {
            URL.revokeObjectURL(this.workerUrl);
            this.workerUrl = undefined;
        }
    }
}

const SEMANTIC_GRAPH_WORKER_SOURCE = String.raw`
const TRANSFORMERS_MODULE_URL = '${TRANSFORMERS_MODULE_URL}';
const TRANSFORMERS_WASM_PATH = '${TRANSFORMERS_WASM_PATH}';
const EMBEDDING_MODEL_ID = '${EMBEDDING_MODEL_ID}';
const MAX_MODEL_TEXT_LENGTH = ${MAX_MODEL_TEXT_LENGTH};

let extractorPromise;
const vectorCache = new Map();

const embedQuery = async (text) => embedOne('query: ' + compactText(text));

const embedPassages = async (texts) => {
    const inputs = texts.map((text) => 'passage: ' + compactText(text));
    const missingInputs = inputs.filter((input) => !vectorCache.has(input));

    if (missingInputs.length) {
        const vectors = await embedBatch(missingInputs);
        missingInputs.forEach((input, index) => vectorCache.set(input, vectors[index]));
    }

    return inputs.map((input) => vectorCache.get(input) || []);
};

const embedOne = async (text) => {
    if (vectorCache.has(text)) {
        return vectorCache.get(text) || [];
    }

    const vectors = await embedBatch([text]);
    const vector = vectors[0] || [];
    vectorCache.set(text, vector);
    return vector;
};

const embedBatch = async (texts) => {
    const extractor = await loadExtractor();
    const output = await extractor(texts, { pooling: 'mean', normalize: true });
    return toVectors(output);
};

const loadExtractor = async () => {
    if (!extractorPromise) {
        extractorPromise = (async () => {
            const transformers = await import(TRANSFORMERS_MODULE_URL);
            transformers.env.allowLocalModels = false;
            transformers.env.allowRemoteModels = true;
            transformers.env.backends.onnx.wasm.wasmPaths = TRANSFORMERS_WASM_PATH;
            return await transformers.pipeline('feature-extraction', EMBEDDING_MODEL_ID);
        })().catch((error) => {
            extractorPromise = undefined;
            throw error;
        });
    }

    return extractorPromise;
};

const compactText = (text) => (text || '').replace(/\s+/g, ' ').trim().slice(0, MAX_MODEL_TEXT_LENGTH);

const toVectors = (output) => {
    const data = Array.from(output.data);
    const dims = output.dims || [];

    if (dims.length >= 2) {
        const width = dims[dims.length - 1];
        const count = Math.max(1, Math.floor(data.length / width));
        const vectors = [];

        for (let index = 0; index < count; index += 1) {
            vectors.push(normalizeVector(data.slice(index * width, (index + 1) * width)));
        }

        return vectors;
    }

    return [normalizeVector(data)];
};

const normalizeVector = (vector) => {
    const magnitude = Math.sqrt(vector.reduce((sum, value) => sum + value * value, 0)) || 1;
    return vector.map((value) => value / magnitude);
};

const describeEntry = (entry) => [
    entry.title,
    entry.category,
    entry.summary,
    (entry.keywords || []).join(' '),
].filter(Boolean).join('. ');

const dot = (left, right) => {
    const length = Math.min(left.length, right.length);
    let sum = 0;

    for (let index = 0; index < length; index += 1) {
        sum += left[index] * right[index];
    }

    return sum;
};

const rankEntries = async (seed, entries) => {
    const queryVector = await embedQuery(seed);
    const passageTexts = entries.map((entry) => describeEntry(entry));
    const passageVectors = await embedPassages(passageTexts);
    const similarities = entries.map((entry, index) => ({
        entry,
        similarity: dot(queryVector, passageVectors[index] || []),
    })).sort((left, right) => {
        if (right.similarity !== left.similarity) {
            return right.similarity - left.similarity;
        }

        return left.entry.title.localeCompare(right.entry.title);
    });

    const max = similarities[0]?.similarity || 0;
    const min = similarities[similarities.length - 1]?.similarity || 0;
    const range = Math.max(0.0001, max - min);

    return similarities.map((item) => {
        const relative = Math.round(((item.similarity - min) / range) * 100);
        const score = Math.max(1, Math.min(99, relative));

        return {
            entry: item.entry,
            score,
            relevance: score,
            matchedTerms: ['multilingual-e5-small'],
        };
    });
};

self.onmessage = async (event) => {
    const message = event.data || {};

    if (message.type !== 'rank') {
        return;
    }

    try {
        const items = await rankEntries(message.seed || '', message.entries || []);
        self.postMessage({
            id: message.id,
            type: 'rank',
            ok: true,
            items,
        });
    } catch (error) {
        self.postMessage({
            id: message.id,
            type: 'rank',
            ok: false,
            error: error && error.message ? error.message : String(error),
        });
    }
};
`;

export class SemanticGraphModel<T extends SemanticGraphEntry> {
    private static readonly workerClient = new SemanticGraphWorkerClient();

    public async rank(seed: string, entries: T[]): Promise<SemanticGraphRankedEntry<T>[]> {
        return await SemanticGraphModel.workerClient.rank(seed, entries) as SemanticGraphRankedEntry<T>[];
    }
}
